import { useState } from "react";
import { Button, Grid, Modal, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { request } from "@/Api";
import {
    selectIsLoggedIn,
    selectIsSuperAdmin,
    selectIsOrganizationInitialized,
    selectOrganizationId,
    setToken,
    selectIsVerified,
    selectSubscriptionActive,
} from "@/auth/authSlice";
import useEntitlements from "@/auth/useEntitlements";
import { CustomBox } from "@/common/StyledComponents";
import { PageMessage } from "@/common/typography";
import { setError } from "@/nav/navSlice";

export default function ProtectedContainer({
    container,
    permission,
    requireSuperAdmin,
    feature,
}) {
    const { organizationId } = useParams();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentOrganizationId = useSelector(selectOrganizationId);
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const isSuperAdmin = useSelector(selectIsSuperAdmin);
    const isOrganizationInitialized = useSelector(
        selectIsOrganizationInitialized,
    );
    const isAccountVerified = useSelector(selectIsVerified);
    const isSubscriptionActive = useSelector(selectSubscriptionActive);
    const { hasFeature, hasPermission: checkPermission } = useEntitlements();
    const hasPermission = permission ? checkPermission(permission) : true;

    const enterOrganization = () => {
        request
            .post(`enter-organization/${organizationId}`)
            .then((response) => {
                dispatch(setToken(response.data));
            })
            .catch(() => {
                dispatch(
                    setError({
                        message:
                            "You don't have access to this organization, going back to dashboard",
                    }),
                );
                navigate("/dashboard");
            });
    };
    if (!isLoggedIn) {
        localStorage.setItem("savedPath", window.location.pathname);
        return <Navigate to={"/login"}></Navigate>;
    }
    if (!isSubscriptionActive) {
        return <Navigate to={"/payment"}></Navigate>;
    }
    if (!isAccountVerified) {
        return <Navigate to={"/verify"}></Navigate>;
    }
    if (!isOrganizationInitialized) {
        return <Navigate to={"/initializing"}></Navigate>;
    }

    if (
        (permission && !hasPermission) ||
        (requireSuperAdmin && !isSuperAdmin)
    ) {
        return (
            <PageMessage>
                You do not have permission to view this page.
            </PageMessage>
        );
    }

    if (feature && !hasFeature(feature)) {
        return <Navigate to={"/dashboard"}></Navigate>;
    }

    if (organizationId && organizationId != currentOrganizationId) {
        return <EnterOrganizationPopup enterOrganization={enterOrganization} />;
    }

    return <>{container}</>;
}

const EnterOrganizationPopup = ({ enterOrganization }) => {
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const theme = useTheme();
    const close = () => {
        setOpen(false);
        navigate("/dashboard");
    };
    const confirm = () => {
        enterOrganization();
        setOpen(false);
    };
    return (
        <>
            <Modal open={open} onClose={close}>
                <CustomBox
                    style={{
                        background: theme.palette.background.paper,
                        padding: 40,
                        paddingBottom: 0,
                        overflow: "auto",
                    }}
                >
                    <h1>This is a URL to another organization</h1>
                    <p>
                        Are you sure you wish to switch your active organization
                        to this one?
                    </p>
                    <Grid
                        container
                        spacing={2}
                        marginBottom={8}
                        justifyContent={"right"}
                    >
                        <Grid item>
                            <Button color="success" onClick={confirm}>
                                Yes
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button color="secondary" onClick={close}>
                                No
                            </Button>
                        </Grid>
                    </Grid>
                </CustomBox>
            </Modal>
        </>
    );
};
